import { useSyncLag } from '@/hooks/use-kitchen';
import type { SecureScaleCopyFacts, SecureScaleCopyVersionFact } from '@/hooks/use-terminals';
import { useT } from '@/i18n/use-t';

/** The shape `useT` returns, for the helpers below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

/** `2026-10-14 08:30 UTC`, the lane's own format: unambiguous in any zone. */
function instant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const two = (v: number) => String(v).padStart(2, '0');
  return `${d.getUTCFullYear()}-${two(d.getUTCMonth() + 1)}-${two(d.getUTCDate())} ${two(
    d.getUTCHours(),
  )}:${two(d.getUTCMinutes())} UTC`;
}

function relative(iso: string | null, t: Translate): string {
  if (iso === null) return t('terminals.relative.never', 'never');
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  const ago = (key: string, en: string, n: number) => t(key, en).replace('{n}', String(n));
  if (seconds < 60) return ago('terminals.relative.secondsAgo', '{n}s ago', seconds);
  if (seconds < 3600)
    return ago('terminals.relative.minAgo', '{n} min ago', Math.floor(seconds / 60));
  if (seconds < 86_400)
    return ago('terminals.relative.hAgo', '{n} h ago', Math.floor(seconds / 3600));
  return ago('terminals.relative.dAgo', '{n} d ago', Math.floor(seconds / 86_400));
}

function VersionLine({ fact, t }: { fact: SecureScaleCopyVersionFact | null; t: Translate }) {
  if (fact === null) return <>{t('terminals.secureScaleCopy.none', 'none')}</>;
  const created =
    fact.createdAt === null
      ? t('terminals.secureScaleCopy.notRecorded', 'not recorded')
      : instant(fact.createdAt);
  return (
    <>
      {t('terminals.secureScaleCopy.version', 'Version {version} (revision {revision})')
        .replace('{version}', fact.versionId)
        .replace('{revision}', String(fact.revision))}
      {' · '}
      <span title={fact.createdAt ?? undefined}>
        {t('terminals.secureScaleCopy.createdAt', 'built {at}').replace('{at}', created)}
      </span>
      <span className="block text-xs text-muted-foreground">
        {t(
          'terminals.secureScaleCopy.sourceReplicaBundle',
          'source: the weight copy built centrally',
        )}
      </span>
    </>
  );
}

function Facts({ copy, t }: { copy: SecureScaleCopyFacts; t: Translate }) {
  const checking = copy.checking;
  const synced = copy.lastCompleteSync.at;
  return (
    <dl className="grid gap-x-6 gap-y-1 text-sm md:grid-cols-[max-content_1fr]">
      <dt className="text-muted-foreground">
        {t('terminals.secureScaleCopy.checking', 'In use for checks')}
      </dt>
      <dd data-testid="securescale-copy-checking">
        <VersionLine fact={checking} t={t} />
        {checking !== null && (
          <span className="block text-xs text-muted-foreground">
            {checking.basis === 'pinned'
              ? t('terminals.secureScaleCopy.basisPinned', 'pinned to the open sale')
              : t('terminals.secureScaleCopy.basisActive', 'active')}
          </span>
        )}
      </dd>
      <dt className="text-muted-foreground">
        {t('terminals.secureScaleCopy.pending', 'Waiting (not yet in use)')}
      </dt>
      <dd data-testid="securescale-copy-pending">
        <VersionLine fact={copy.pending} t={t} />
      </dd>
      <dt className="text-muted-foreground">
        {t('terminals.secureScaleCopy.lastCompleteSync', 'Last successful complete sync')}
      </dt>
      <dd data-testid="securescale-copy-last-sync" title={synced ?? undefined}>
        {synced === null
          ? t('terminals.secureScaleCopy.notRecorded', 'not recorded')
          : instant(synced)}
        <span className="block text-xs text-muted-foreground">
          {t('terminals.secureScaleCopy.sourceLaneClock', 'source: lane clock')}
        </span>
      </dd>
    </dl>
  );
}

/**
 * SSC-260: the SecureScale weight copy an SCO lane reports (SSC-230), from
 * the last telemetry snapshot. Rendered only when the lane reported the
 * block, so other terminal pages are unchanged. Information for staff only:
 * no level, no colour and no threshold, and nothing here changes a check.
 */
export function SecureScaleCopyCard({ terminalId }: { terminalId: string }) {
  const t = useT('admin_ui');
  const lag = useSyncLag(terminalId);
  const copy = lag.data?.secureScaleCopy;
  if (copy === undefined) return null;
  return (
    <section className="rounded-md border bg-card p-5" data-testid="securescale-copy-card">
      <h2 className="mb-2 text-sm font-medium">
        {t('terminals.secureScaleCopy.title', 'SecureScale weight copy')}
      </h2>
      <Facts copy={copy} t={t} />
      <p className="mt-3 text-xs text-muted-foreground">
        {t(
          'terminals.secureScaleCopy.reported',
          'Reported by the lane {when}. Information only: nothing here changes a check.',
        ).replace('{when}', relative(lag.data?.reportedAt ?? null, t))}
      </p>
    </section>
  );
}
